import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../../components/context/LoginContext/AuthContext";

/**
 * @typedef {Object} Institution
 * @property {number} id
 * @property {string} name
 * @property {string|null} image_url
 */

/**
 * @typedef {Object} AgreementInvoice
 * @property {number} id
 * @property {string} tenant_id
 * @property {number} institution_id
 * @property {number} cycle_id
 * @property {string} invoice_no
 * @property {number} total_amount
 * @property {number} paid_amount
 * @property {string} issued_at
 * @property {string} status
 * @property {Institution} institution
 */

/**
 * Admin - Get All School Agreement Invoices (with filter & pagination)
 */
export const useGetAllAgreementInvoices = ({
  status = "all",
  page = 1,
  perPage = 10,
} = {}) => {
  const { user, fetchWithAuth } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  /** @type {[AgreementInvoice[], Function]} */
  const [agreementInvoices, setAgreementInvoices] = useState([]);
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAgreementInvoices = useCallback(
    async (customParams = {}) => {
      if (!user?.token) return;

      const finalStatus = customParams.status ?? status;
      const finalPage = customParams.page ?? page;
      const finalPerPage = customParams.perPage ?? perPage;

      const query = new URLSearchParams();
      if (finalStatus && finalStatus !== "all") {
        query.append("filter[status]", finalStatus);
      }
      query.append("page", finalPage);
      query.append("per_page", finalPerPage);

      setLoading(true);
      setError(null);

      try {
        const response = await fetchWithAuth(
          `${apiUrl}/school/invoices?${query.toString()}`,
          { method: "GET" }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch invoices");
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.message || "Failed to fetch invoices");
        }

        setAgreementInvoices(result.data || []);
        setMeta(result.meta || null);
        setStats(result.stats || null);
      } catch (err) {
        console.error("Invoices Fetch Error:", err);
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
    [apiUrl, fetchWithAuth, user, status, page, perPage]
  );

  useEffect(() => {
    fetchAgreementInvoices();
  }, [fetchAgreementInvoices]);

  return {
    agreementInvoices,
    meta,
    stats,
    loading,
    error,
    refetch: fetchAgreementInvoices, // can pass {status, page, perPage}
  };
};
